import {
	faBan,
	faCartShopping,
	faChartGantt,
	faCheck,
	faCopy,
	faExternalLinkAlt,
	faMoneyBill,
	faPlus,
	faShare,
	faSwatchbook,
	faUndo,
} from "@fortawesome/pro-regular-svg-icons";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import i18n from "i18next";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { ProjectGanttView } from "src/components/views/erp/project/gantt/ProjectGanttView.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { ProjectAllActivityMaterialsDataGrid } from "src/components/views/erp/project/projectActivity/material/ProjectAllActivityMaterialsDataGrid.tsx";
import { ProjectView } from "src/api/generated/erp/db/types/tables/projectView.ts";
import { OpenObjectChangeLogButton } from "src/components/views/changeLogging/OpenObjectChangeLogButton.tsx";
import { ProjectsContainerView } from "src/components/views/erp/project/ProjectsContainerView.tsx";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { useFormInput } from "src/components/common/dialogs/formInput/useFormInput.tsx";
import { ProjectActionsApi } from "src/api/generated/erp/project/project/api/projectActionsApi.ts";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { ProjectCostEventsDataGrid } from "src/components/views/erp/project/project/ProjectCostEventsDataGrid.tsx";
import { openFormOnDialog } from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import { CreateNewSubProjectForm } from "src/components/views/erp/project/subProject/CreateNewSubProjectForm.tsx";
import { IsoDateString } from "src/types/dateTime.ts";
import { requireRule } from "src/components/common/forms/validation.ts";
import { FormDateField } from "src/components/common/forms/fields/FormDateField.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { deCapitalize } from "src/utils/strings.tsx";
import { OpenConnectedWorkspacesButton } from "src/components/views/erp/utilComponents/OpenConnectedWorkspacesButton.tsx";
import { OpenCustomerOrderButton } from "src/components/views/erp/utilComponents/OpenCustomerOrderButton.tsx";
import { getOnlyMember } from "src/utils/arrayUtils.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { ProjectLinkedPurchaseOrdersDataGrid } from "src/components/views/erp/project/project/ProjectLinkedPurchaseOrdersDataGrid.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ProjectContextMenuProps {
	allSelectedProjects: ProjectView[];
	refreshData?: () => Promise<unknown>;
}

export const ProjectContextMenu = ({ allSelectedProjects, refreshData }: ProjectContextMenuProps) => {
	const { openDialog } = useGenericDialog();
	const showFormInput = useFormInput();
	const showConfirmDialog = useConfirmDialog();
	const tenantConfig = useTenantCustomizations().tenantConfig;
	const hasPermission = useHasPermission();

	const onlySelectedProject = getOnlyMember(allSelectedProjects);

	return [
		onlySelectedProject && (
			<AsyncMenuButton
				key={"openProject"}
				label={i18n.t("open")}
				icon={faExternalLinkAlt}
				onClick={() => {
					openDialog({
						title: i18n.t("project"),
						content: <ProjectsContainerView onlyProjectId={onlySelectedProject.projectId} />,
						agentContext: {
							key: defineAgentUiContextKey("erp.project.project.dialog"),
							title: i18n.t("project"),
							pathSegments: [onlySelectedProject.projectDescription],
							data: {
								projectId: onlySelectedProject.projectId,
							},
						},
					});
				}}
			/>
		),
		<AsyncMenuButton
			key={"gantt"}
			label={i18n.t("gantt")}
			icon={faChartGantt}
			onClick={() => {
				openDialog({
					title: i18n.t("gantt"),
					content: <ProjectGanttView projectIds={allSelectedProjects.map((p) => p.projectId)} />,
					agentContext: {
						key: defineAgentUiContextKey("erp.project.projectGantt.dialog"),
						title: i18n.t("gantt"),
						data: {
							projectIds: allSelectedProjects.map((p) => p.projectId),
						},
					},
				});
			}}
		/>,
		onlySelectedProject?.projectState === "INITIAL" && (
			<AsyncMenuButton key={"release"} label={i18n.t("release")} icon={faShare} onClick={onReleaseProjectClick} />
		),
		onlySelectedProject?.projectState === "RELEASED" && (
			<AsyncMenuButton
				key={"complete"}
				label={i18n.t("mark_as_completed")}
				icon={faCheck}
				onClick={async () => {
					const confirmed = await showConfirmDialog({});
					if (!confirmed) return;
					await ProjectActionsApi.completeProject({ projectId: onlySelectedProject!.projectId });
					await refreshData?.();
				}}
			/>
		),
		onlySelectedProject?.projectState === "READY" && (
			<AsyncMenuButton
				key={"revertComplete"}
				label={i18n.t("revert_complete")}
				icon={faUndo}
				onClick={async () => {
					await ProjectActionsApi.revertCompleteProject({ projectId: onlySelectedProject!.projectId });
					await refreshData?.();
				}}
			/>
		),
		onlySelectedProject && ["INITIAL", "RELEASED"].includes(onlySelectedProject.projectState) && (
			<AsyncMenuButton
				key={"cancel"}
				label={i18n.t("cancel")}
				icon={faBan}
				onClick={async () => {
					const confirmed = await showConfirmDialog({
						message: i18n.t("confirm_cancel_project"),
					});
					if (!confirmed) return;
					await ProjectActionsApi.cancelProject({ projectId: onlySelectedProject!.projectId });
					await refreshData?.();
				}}
			/>
		),
		onlySelectedProject && ["INITIAL", "RELEASED"].includes(onlySelectedProject.projectState) && (
			<AsyncMenuButton
				key={"addSubProject"}
				label={i18n.t("add_sub_project")}
				icon={faPlus}
				onClick={() => {
					openFormOnDialog({
						openDialog,
						title: i18n.t("new_sub_project"),
						size: "sm",
						component: CreateNewSubProjectForm,
						props: (closeDialog) => ({
							projectId: onlySelectedProject!.projectId,
							closeDialog: closeDialog,
						}),
						agentContext: {
							key: defineAgentUiContextKey("erp.project.subProject.formDialog"),
							title: i18n.t("new_sub_project"),
							pathSegments: [onlySelectedProject.projectDescription],
							data: {
								projectId: onlySelectedProject.projectId,
							},
						},
						confirmCloseIfEdited: false,
						onSubmit: refreshData,
					});
				}}
			/>
		),
		onlySelectedProject && (
			<AsyncMenuButton
				key={"clone"}
				label={i18n.t("create_a_copy")}
				icon={faCopy}
				onClick={onCloneProjectClick}
			/>
		),
		onlySelectedProject && (
			<DocumentsOfObjectButton
				key={"documents"}
				variant={"menu"}
				label={i18n.t("documents")}
				objectRef={{ objectType: "PROJECT", objectId: onlySelectedProject.projectId }}
			/>
		),
		onlySelectedProject && (
			<AsyncMenuButton
				key={"materials"}
				label={i18n.t("materials")}
				icon={faSwatchbook}
				onClick={() => {
					openDialog({
						title: i18n.t("materials"),
						content: <ProjectAllActivityMaterialsDataGrid projectId={onlySelectedProject.projectId} />,
						agentContext: {
							key: defineAgentUiContextKey("erp.project.projectMaterials.dialog"),
							title: i18n.t("materials"),
							pathSegments: [onlySelectedProject.projectDescription],
							data: {
								projectId: onlySelectedProject.projectId,
							},
						},
					});
				}}
			/>
		),
		onlySelectedProject && hasPermission(Permissions.Purchasing.PurchaseOrders.READ) && (
			<AsyncMenuButton
				key={"connectedPurchaseOrders"}
				label={i18n.t("connected_purchase_orders")}
				icon={faCartShopping}
				onClick={() => {
					openDialog({
						title: i18n.t("connected_purchase_orders"),
						size: "xl",
						content: <ProjectLinkedPurchaseOrdersDataGrid projectId={onlySelectedProject.projectId} />,
						agentContext: {
							key: defineAgentUiContextKey("erp.project.linkedPurchaseOrders.dialog"),
							title: i18n.t("connected_purchase_orders"),
							pathSegments: [onlySelectedProject.projectDescription],
							data: {
								projectId: onlySelectedProject.projectId,
							},
						},
					});
				}}
			/>
		),
		onlySelectedProject?.costEventPriceListId != null && (
			<AsyncMenuButton
				key={"costEvents"}
				label={i18n.t("cost_events")}
				icon={faMoneyBill}
				onClick={() => {
					openDialog({
						title: i18n.t("cost_events"),
						size: "xl",
						content: <ProjectCostEventsDataGrid projectId={onlySelectedProject.projectId} />,
						agentContext: {
							key: defineAgentUiContextKey("erp.project.projectCostEvents.dialog"),
							title: i18n.t("cost_events"),
							pathSegments: [onlySelectedProject.projectDescription],
							data: {
								projectId: onlySelectedProject.projectId,
							},
						},
					});
				}}
			/>
		),
		onlySelectedProject?.customerOrderId != null && (
			<OpenCustomerOrderButton key={"openCustomerOrder"} customerOrderId={onlySelectedProject.customerOrderId} />
		),
		onlySelectedProject && tenantConfig.workspacesEnabled && (
			<OpenConnectedWorkspacesButton
				key={"workspaces"}
				connectedItemRef={{
					itemType: "PROJECT",
					itemId: onlySelectedProject.projectId,
				}}
			/>
		),
		onlySelectedProject && (
			<OpenObjectChangeLogButton
				key={"changeLog"}
				objectRef={{ objectType: "PROJECT", objectId: onlySelectedProject.projectId }}
			/>
		),
	];

	async function onReleaseProjectClick() {
		if (!onlySelectedProject) return;

		interface FormInput {
			releaseSubProjectsAndActivities: boolean;
			releaseTasks: boolean;
		}

		const input = await showFormInput<FormInput>({
			title: i18n.t("release_project"),
			defaultValues: {
				releaseSubProjectsAndActivities: false,
				releaseTasks: false,
			},
			submitLabel: i18n.t("release"),
			content: ({ control, watch }) => (
				<>
					<FormCheckbox
						control={control}
						name={"releaseSubProjectsAndActivities"}
						label={i18n.t("release_also_sub_projects_and_activities")}
					/>
					{watch("releaseSubProjectsAndActivities") && (
						<FormCheckbox control={control} name={"releaseTasks"} label={i18n.t("release_also_tasks")} />
					)}
				</>
			),
		});
		if (input === undefined) return;

		await ProjectActionsApi.releaseProject({
			projectId: onlySelectedProject.projectId,
			releaseSubProjectsAndActivities: input.releaseSubProjectsAndActivities,
			releaseTasks: input.releaseTasks,
		});
		await refreshData?.();
	}

	async function onCloneProjectClick() {
		if (!onlySelectedProject) return;

		const formInput = await showFormInput<{ name: string; plannedStartDate: IsoDateString }>({
			title: i18n.t("clone_project"),
			submitLabel: i18n.t("clone"),
			content: ({ control }) => (
				<>
					<FormTextField control={control} name={"name"} label={i18n.t("name")} rules={requireRule()} />
					<FormDateField
						control={control}
						name={"plannedStartDate"}
						label={i18n.t("planned_start")}
						rules={requireRule()}
					/>
				</>
			),
			defaultValues: {
				name: onlySelectedProject.projectDescription + " -" + deCapitalize(i18n.t("copy.noun")),
			},
		});
		if (!formInput) return;

		await ProjectActionsApi.cloneProject({
			projectId: onlySelectedProject.projectId,
			newProjectPlannedStartDate: formInput.plannedStartDate,
			newProjectName: formInput.name,
		});
		await refreshData?.();
	}
};
